import React, { memo } from 'react';
import { TrendingUp, TrendingDown, Activity, BarChart3, Loader2 } from 'lucide-react';

// ============================================
// COMPACT SESSION STATS PANEL - Bloomberg Dense
// ============================================
export const SessionStats = memo(({ stats, latestCandle, ltp, currentSymbol = 'NIFTY', isLoading = false }) => {
  const {
    cumulative_delta = 0,
    session_volume = 0,
    vwap = 0,
    tick_count = 0,
    alert_count = 0,
  } = stats;

  // Format numbers - SESSION 130: Show full volume with commas instead of L/K shorthand
  const formatNumber = (num, decimals = 0) => {
    return num.toLocaleString(undefined, { maximumFractionDigits: decimals });
  };

  // Use LTP if available, otherwise fall back to candle close
  const displayPrice = ltp?.price || latestCandle?.close;
  const priceDirection = ltp ? (ltp.side === 'ASK' ? 'buy' : 'sell') : 
    (latestCandle ? (latestCandle.close >= latestCandle.open ? 'buy' : 'sell') : 'default');

  const deltaVariant = cumulative_delta > 0 ? 'text-buy' : cumulative_delta < 0 ? 'text-sell' : 'text-foreground';

  // Show skeleton loading during symbol transition
  if (isLoading) {
    return (
      <div className="space-y-1 animate-pulse" data-testid="session-stats-loading">
        {/* OHLC placeholder */}
        <div className="flex items-center justify-between border-b border-border pb-1">
          <div className="h-2.5 w-10 bg-surface-highlight rounded" />
          <div className="h-2.5 w-32 bg-surface-highlight rounded" />
        </div>
        {/* Metrics placeholder */}
        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
          {[1,2,3,4].map(i => (
            <div key={i} className="flex justify-between items-center">
              <div className="h-2.5 w-6 bg-surface-highlight rounded" />
              <div className="h-3 w-10 bg-surface-highlight rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="session-stats">
      {/* OHLC Row - Current Candle Info - Compact */}
      {latestCandle && (
        <div className="flex items-center justify-between text-[9px] font-mono text-muted-foreground border-b border-border pb-1">
          <span className="text-[10px] text-accent font-bold">{currentSymbol}</span>
          <div className="flex items-center gap-2">
            <span>O:{latestCandle.open.toFixed(2)}</span>
            <span>H:{latestCandle.high.toFixed(2)}</span>
            <span>L:{latestCandle.low.toFixed(2)}</span>
            <span className={`font-bold ${latestCandle.close >= latestCandle.open ? 'text-buy' : 'text-sell'}`}>
              C:{latestCandle.close.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* Metrics Grid - 2x2 Ultra Compact */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
        <div className="flex justify-between items-center">
          <span className="text-[8px] text-muted-foreground uppercase">CVD</span>
          <span className={`font-mono text-[11px] font-bold ${deltaVariant}`}>
            {formatNumber(cumulative_delta)}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[8px] text-muted-foreground uppercase">VWAP</span>
          <span className="font-mono text-[11px] font-bold text-foreground">
            {vwap.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[8px] text-muted-foreground uppercase">Vol</span>
          <span className="font-mono text-[11px] text-foreground">
            {formatNumber(session_volume)}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[8px] text-muted-foreground uppercase">Alerts</span>
          <span className={`font-mono text-[11px] ${alert_count > 0 ? 'text-warning' : 'text-foreground'}`}>
            {alert_count}
          </span>
        </div>
      </div>

      {/* Delta Bar - Compact */}
      {latestCandle && (
        <div className="flex items-center gap-1.5 pt-0.5 border-t border-border">
          <span className="text-[10px] text-muted-foreground uppercase">Δ</span>
          <div className={`font-mono text-sm font-bold ${latestCandle.delta >= 0 ? 'text-buy' : 'text-sell'}`}>
            {latestCandle.delta >= 0 ? '+' : ''}{latestCandle.delta.toLocaleString()}
          </div>
          <div className="flex-1 h-1.5 bg-surface-highlight rounded-none overflow-hidden flex">
            <div 
              className="h-full bg-buy transition-all duration-150"
              style={{ 
                width: `${Math.max(0, Math.min(100, (latestCandle.delta / (Math.abs(latestCandle.delta) + 100)) * 100))}%` 
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
});

SessionStats.displayName = 'SessionStats';

export default SessionStats;
